import 'leaflet/dist/leaflet.css';
import type React from 'react';
import { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';

const DEFAULT_CENTER: [number, number] = [-6.369, 34.889];
const DEFAULT_ZOOM = 6;
const MAP_HEIGHT = 320;

export interface PlotWithPolygon {
  id?: number;
  polygon?: string;
  latitude?: number;
  longitude?: number;
}

interface PlotPolygonsMapProps {
  plots: PlotWithPolygon[];
  style?: React.CSSProperties;
}

function parseGeoJSON(value: string): GeoJSON.GeoJsonObject | null {
  if (!value || typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as GeoJSON.GeoJsonObject;
    if (
      parsed &&
      (parsed.type === 'Feature' ||
        parsed.type === 'FeatureCollection' ||
        parsed.type === 'Point' ||
        parsed.type === 'Polygon' ||
        parsed.type === 'MultiPolygon' ||
        parsed.type === 'LineString' ||
        parsed.type === 'GeometryCollection')
    ) {
      return parsed;
    }
    if ((parsed as GeoJSON.Feature).geometry) {
      return parsed as GeoJSON.Feature;
    }
    return parsed;
  } catch {
    return null;
  }
}

function FitBounds({ geojsonObjects }: { geojsonObjects: GeoJSON.GeoJsonObject[] }) {
  const map = useMap();
  useEffect(() => {
    if (geojsonObjects.length === 0) return;
    const bounds = L.latLngBounds([]);
    geojsonObjects.forEach((data) => {
      const layer = L.geoJSON(data);
      layer.eachLayer((l) => {
        const b = (l as L.Layer).getBounds?.();
        if (b) bounds.extend(b);
      });
    });
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14 });
  }, [map, geojsonObjects]);
  return null;
}

export const PlotPolygonsMap: React.FC<PlotPolygonsMapProps> = ({ plots, style }) => {
  const { geojsonObjects, hasAny } = useMemo(() => {
    const objects: GeoJSON.GeoJsonObject[] = [];
    plots.forEach((plot) => {
      if (plot.polygon) {
        const parsed = parseGeoJSON(plot.polygon);
        if (parsed) objects.push(parsed);
      }
    });
    return { geojsonObjects: objects, hasAny: objects.length > 0 };
  }, [plots]);

  if (!hasAny) {
    return (
      <div
        style={{ height: MAP_HEIGHT, position: 'relative', ...style }}
        className="plot-polygons-map plot-polygons-map--empty"
      >
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </MapContainer>
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.9)',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          No polygon data for plots
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: MAP_HEIGHT, width: '100%', ...style }} className="plot-polygons-map">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds geojsonObjects={geojsonObjects} />
        {geojsonObjects.map((data, idx) => (
          <GeoJSON
            key={idx}
            data={data}
            style={{
              color: '#1890ff',
              weight: 2,
              fillColor: '#1890ff',
              fillOpacity: 0.25,
            }}
          />
        ))}
      </MapContainer>
    </div>
  );
};
